import React from 'react';
import { EventNameNav, EventNameNavProps } from '../components/EventNameNav';
import type { WebsiteHeaderLayout_Monogram } from '@shared/types/WebsiteHeaderSettings.types';
import { HeaderPhoto } from '../components/HeaderPhoto/HeaderPhoto';
import { useJsonDataContainerByIdQuery } from '@graphql/generated';
import { Box, Flex } from '@withjoy/joykit';
import type { ThemeJson } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { PPI } from '@apps/card/routes/CardCustomizer/steps/CardDesign/config';
import { useColorPaletteProvider } from '../../../../ColorPaletteProvider/ColorPaletteProvider';
import { LazyLoadedMonogramView } from '@apps/admin/routes/WebsiteDesignerV2/routes/Layout/components/HeaderPhotoCustomization/components/MonogramEditorDialog/MonogramView.lazy';

interface MonogramLayoutProps extends EventNameNavProps {
  websiteHeader: WebsiteHeaderLayout_Monogram;
  showPlaceholder?: boolean;
}

export const MonogramLayout = (props: MonogramLayoutProps) => {
  const { websiteHeader, showPlaceholder = false, ...restProps } = props;

  const layout = websiteHeader.headerPhoto;
  const jsonContainerId = layout.jsonContainerId;

  const { data } = useJsonDataContainerByIdQuery({
    variables: {
      payload: {
        id: jsonContainerId as string,
      },
    },
    batchMode: 'fast',
  });

  const jsonContent = data?.jsonDataContainerById?.content;

  return (
    <EventNameNav
      eventDisplayName={websiteHeader.eventDisplayName}
      {...restProps}
    >
      <HeaderPhoto
        websiteHeader={websiteHeader}
        pageSlug={restProps.pageSlug}
        pageType={restProps.page}
        showPlaceholder={showPlaceholder}
      >
        <Monogram
          config={jsonContent}
          showPlaceholder={showPlaceholder}
        />
      </HeaderPhoto>
    </EventNameNav>
  );
};

const SIZE = 6 * PPI;

const Monogram = ({ config, showPlaceholder }: { config?: ThemeJson; showPlaceholder?: boolean }) => {
  const { pageColor, pageFontColor } = useColorPaletteProvider();

  if (!config) {
    return showPlaceholder ? (
      <Flex
        justifyContent="center"
        alignItems="center"
        width="100%"
      >
        <Box
          width={SIZE}
          height={SIZE}
          backgroundColor="lightgray"
        />
      </Flex>
    ) : null;
  }

  return (
    <Flex
      justifyContent="center"
      alignItems="center"
      width="100%"
    >
      <LazyLoadedMonogramView
        json={config}
        background={pageColor}
        fontColor={pageFontColor}
      />
    </Flex>
  );
};
